import { useEffect, useMemo, useState } from "preact/hooks";
import { labelsMetadata, LabelTemplateFields } from "../api/label-creator";
import { addRevisionedProductToDatabase, Label, ProductTypeRevision } from "../api/product-api";
import { TemplatePrint } from "./TemplatePrint";
import {
    fillLabelFields,
    fillLabelFieldsAsync,
    findSerialNumberByRegex,
    generateExampleFieldsForLabel,
} from "../utils";
import { resolvePath } from "../api";
import { h, Fragment } from "preact";
import { Command } from "../hooks/Command";
import { ScanPackQR } from "./ScanPackQR";
import { serial8Base32 } from "../data/serialNumberSearchExecs";
import { useForceUpdate } from "../hooks";
import { TemplateMultipartPrint } from "./TemplateMultipartPrint";

export function LabelPrint(props: {
    label: Label;
    copies: number;
    onProvideFields?: (
        index: number,
        template?: number,
        cache?: Record<string, unknown>,
    ) =>
        | { fields: LabelTemplateFields; overrideFields?: { [key: string]: string } }
        | Promise<{ fields: LabelTemplateFields; overrideFields?: { [key: string]: string } }>;
    productTypeRevision?: ProductTypeRevision;
    doRefresh?: Command;
    eachProvideFieldsCopy?: number;
    noAutoCut?: boolean;
    onPrint?: () => void;
    serialNumber?: string;
}) {
    const [templateMetadata, setTemplateMetadata] = useState(null as null | { [key: string]: string }[]);
    const [generatedFields, setGeneratedFields] = useState([] as { [key: string]: any }[]);
    const [missingFields, setMissingFields] = useState(
        null as
            | null
            | {
                  key: string;
                  name: string;
                  type: string;
                  value: string | null;
              }[],
    );
    const [focusedMissingField, setFocusedMissingField] = useState(
        null as null | {
            key: string;
            name: string;
            type: string;
            value: string | null;
        },
    );
    const forceUpdate = useForceUpdate();

    const missingFieldsFilled = missingFields
        ? missingFields.reduce((prev, curr) => prev && Boolean(curr.value), true)
        : false;
    const missingFieldsObject = missingFields
        ? missingFields.reduce(
              (prev, curr) => (curr.value ? ((prev[curr.key] = curr.value), prev) : prev),
              {} as { [key: string]: string },
          )
        : {};

    const labels = useMemo(
        () => (props.label.type === "normal" ? [props.label] : (props.label.innerLabels?.map(v => v.label) ?? [])),
        [props.label],
    );

    useEffect(() => {
        if (labels.length === 0) {
            setMissingFields(null);
            setTemplateMetadata(null);
            return;
        }

        if (labels.length > 1) {
            Promise.all(
                labels.map(label =>
                    labelsMetadata(label.path).then(
                        v => ({ ...v, ...label.metadata?.metadata }) as { [key: string]: string },
                    ),
                ),
            ).then(v => {
                setTemplateMetadata(v);

                const fieldsArray = v.map((templateMetadata, index) => {
                    const fields: { [key: string]: any } = {};

                    fillLabelFields(
                        templateMetadata,
                        { serialNumber: "", revision: props.productTypeRevision },
                        fields,
                    );

                    const { "field-serial": _, ...generatedFields } = fields;

                    return generatedFields;
                });

                setGeneratedFields(fieldsArray);
                setMissingFields(null);
            });

            return;
        }

        const label = labels[0];
        const metadata = label.metadata?.metadata;

        labelsMetadata(label.path)
            .then(v => {
                const templateMetadata = { ...v, ...metadata } as { [key: string]: string };
                setTemplateMetadata([templateMetadata]);

                const fields: { [key: string]: any } = {};

                fillLabelFields(templateMetadata, { serialNumber: "", revision: props.productTypeRevision }, fields);

                const { "field-serial": _, ...generatedFields } = fields;

                setGeneratedFields([generatedFields]);

                const missingFields = Object.entries(templateMetadata)
                    .filter(([key]) => key.startsWith("field-") && !(key in fields))
                    .map(([key, value]) => ({
                        key,
                        name: templateMetadata["description-" + key] ?? key,
                        type: value,
                        value: null,
                    }));

                setMissingFields(missingFields);
            })
            .catch(() => {
                setMissingFields(null);
                setTemplateMetadata(null);
            });
    }, [labels]);

    return (
        <>
            {missingFields && missingFields.length > 0
                ? [
                      <h3 class="font-bold">Uzupełnij dane</h3>,
                      <div class="p-2.5 mb-2">
                          <ScanPackQR
                              onRead={json => {
                                  const data = JSON.parse(json);

                                  const field =
                                      missingFields && missingFields.length === 1
                                          ? missingFields[0]
                                          : focusedMissingField;

                                  if (!field) {
                                      return;
                                  }

                                  if (field.type === "serial-8-base32") {
                                      const result = findSerialNumberByRegex(data, serial8Base32.orderedExecs);

                                      if (!result) {
                                          return;
                                      }

                                      field.value = result;
                                      forceUpdate();
                                      return;
                                  }

                                  if (field.type === "text") {
                                      field.value = data;
                                      forceUpdate();
                                      return;
                                  }
                              }}
                          />
                          {missingFields.map(v => (
                              <div style="display: flex; align-items: center;">
                                  <div>{v.name}</div>
                                  <input
                                      key={v.key}
                                      style="margin-left: 10px;"
                                      value={v.value ?? ""}
                                      onChange={e => {
                                          v.value = e.currentTarget.value;
                                          forceUpdate();
                                      }}
                                      onFocus={() => setFocusedMissingField(v)}
                                  ></input>
                              </div>
                          ))}
                      </div>,
                  ]
                : null}
            {missingFieldsFilled && (
                <>
                    {props.label.type === "normal" ? (
                        <TemplatePrint
                            template={props.label.path}
                            fields={{
                                ...generateExampleFieldsForLabel(templateMetadata?.[0] ?? {}),
                                ...generatedFields?.[0],
                                ...missingFieldsObject,
                                ...(props.serialNumber ? { "field-serial": props.serialNumber } : {}),
                            }}
                            copies={props.copies}
                            noAutoCut={props.noAutoCut}
                            tapeImgBase={resolvePath("images/tapes/")}
                            eachProvideFieldsCopy={props.eachProvideFieldsCopy}
                            overrideFields={props.label.metadata?.metadata}
                            onPrint={props.onPrint}
                            onProvideFields={
                                props.onProvideFields ??
                                (async index => {
                                    const fieldsData = {
                                        fields: {},
                                        overrideFields: labels[0].metadata?.metadata,
                                    } as {
                                        fields: { [key: string]: any };
                                        overrideFields?: { [key: string]: string };
                                    };

                                    await fillLabelFieldsAsync(
                                        templateMetadata?.[0]!,
                                        {
                                            serialNumber: async () => {
                                                if (props.serialNumber) {
                                                    return props.serialNumber;
                                                }

                                                if (!props.productTypeRevision) {
                                                    throw new Error("Missing product type revision");
                                                }

                                                const result = await addRevisionedProductToDatabase(
                                                    props.productTypeRevision.id,
                                                );

                                                if (!result?.serialNumber) {
                                                    throw new Error("Missing serial number");
                                                }

                                                return result.serialNumber;
                                            },
                                            revision: props.productTypeRevision,
                                            default: prop => {
                                                if (missingFields) {
                                                    const field = missingFields.find(v => v.key === prop);

                                                    if (!field) {
                                                        return;
                                                    }

                                                    fieldsData.fields[prop] = field.value;
                                                }
                                            },
                                        },
                                        fieldsData.fields,
                                    );

                                    return fieldsData;
                                })
                            }
                        />
                    ) : (
                        <TemplateMultipartPrint
                            templates={props.label.innerLabels?.map(v => v.label.path) ?? []}
                            fields={
                                props.label.innerLabels?.map((v, i) => ({
                                    ...generateExampleFieldsForLabel(templateMetadata?.[i] ?? {}),
                                    ...generatedFields?.[i],
                                    ...missingFieldsObject,
                                    ...(props.serialNumber ? { "field-serial": props.serialNumber } : {}),
                                })) ?? []
                            }
                            uniqueCopies={props.copies}
                            noAutoCut={props.noAutoCut}
                            tapeImgBase={resolvePath("images/tapes/")}
                            copies={props.eachProvideFieldsCopy}
                            overrideFields={props.label.innerLabels?.map(v => v.label.metadata?.metadata ?? {}) ?? []}
                            onPrint={props.onPrint}
                            onProvideFields={
                                props.onProvideFields ??
                                (async (copy, index, cache) => {
                                    const fieldsData = {
                                        fields: {},
                                        overrideFields: labels[index].metadata?.metadata,
                                    } as {
                                        fields: { [key: string]: any };
                                        overrideFields?: { [key: string]: string };
                                    };

                                    await fillLabelFieldsAsync(
                                        templateMetadata?.[index]!,
                                        {
                                            serialNumber: async () => {
                                                if (props.serialNumber) {
                                                    return props.serialNumber;
                                                }

                                                if (!props.productTypeRevision) {
                                                    throw new Error("Missing product type revision");
                                                }

                                                if (cache.serialNumber) {
                                                    return cache.serialNumber as string;
                                                }

                                                const result = await addRevisionedProductToDatabase(
                                                    props.productTypeRevision.id,
                                                );

                                                cache.serialNumber = result.serialNumber;

                                                if (!result?.serialNumber) {
                                                    throw new Error("Missing serial number");
                                                }

                                                return result.serialNumber;
                                            },
                                            revision: props.productTypeRevision,
                                            default: prop => {
                                                if (missingFields) {
                                                    const field = missingFields.find(v => v.key === prop);

                                                    if (!field) {
                                                        return;
                                                    }

                                                    fieldsData.fields[prop] = field.value;
                                                }
                                            },
                                        },
                                        fieldsData.fields,
                                    );

                                    return fieldsData;
                                })
                            }
                        />
                    )}
                </>
            )}
        </>
    );
}
